import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { boundMethod } from 'autobind-decorator';
import { withTranslation } from 'react-i18next';

import { fetchDataForClass, messageTypes } from 'store/modules/chat-messages';
import { setEntitiesSidebar } from 'store/modules/graph/graph';

import { currentOpenModalStates, tutorialNames } from 'config/constants';

import MobileDrawer from './MobileDrawer';
import Sidebar from 'components/sidebar/Sidebar';
import SplitSidebarView from 'components/split/SplitSidebarView';
import KnowledgeGraphSidebar from 'components/sidebar/KnowledgeGraphSidebar';
import MobileHeader from './MobileHeader';

import PageContent from './PageContent';

import { trackEvent } from 'utils/eventTracking';

import styles from './home.scss';
import { searchGraph } from 'store/modules/graph/graphSearchResults';
import withEmbedded from 'root/hocs/withEmbedded';
import { changeUrlGivenCurrentQueryParams } from 'components/LinkWithQuery';
import { fetchAnswerContextMenu } from 'store/modules/answerContextMenu';

const shouldShowKgSidebar = props =>
  props.meta.hasKnowledgeGraphSupport && props.location.search.includes('kgSidebar=true');

const shouldShowVisualGraph = props => shouldShowKgSidebar(props) && props.location.search.includes('visualMode=true');

class Home extends Component {
  constructor(props) {
    super(props);

    this.state = {
      recentlyAddedBoard: this.props.recentlyAddedBoard,
      isQuestionFromURLAsked: false,
      showVisualGraph: false,
      visualGraphHasRendered: false,
      showKgSidebar: false,
      isMobileMenuOpen: false,
      displayModal: null
    };

    this.searchGraphTimeout = null;
  }

  toggleModals = () => {
    const urlParams = new URLSearchParams(this.props.location.search);
    const showDialog = urlParams.get('showDialog');
    if (showDialog === currentOpenModalStates.discovery) {
      this.setState({ displayModal: currentOpenModalStates.discovery });
    } else if (showDialog === currentOpenModalStates.knowledgeGraph) {
      this.setState({ displayModal: currentOpenModalStates.knowledgeGraph });
    } else {
      this.setState({ displayModal: null });
    }
  };

  componentDidMount() {
    this.setState({
      showKgSidebar: shouldShowKgSidebar(this.props),
      showVisualGraph: shouldShowVisualGraph(this.props)
    });

    this.toggleModals();

    /**
     * UGLY HACK ALERT!!
     * Set a global function to support visual context menu.
     *
     * @param selectedPoint is an array of point dimensions
     * [
     *     {
     *         "value": "Zürich",
     *         "candidateId": "Kanton_m32q"
     *     },
     *     {
     *         "value": 28216,
     *         "candidateId": "retCount_yYvT"
     *     }
     * ]
     * @param e         event to get target from, to know what to follow up on.
     */
    window.showAnswerContextMenu = (selectedPoint, e) => {
      // based on https://stackoverflow.com/a/34064434
      const unescapeHtml = input => new DOMParser().parseFromString(input, 'text/html').documentElement.textContent;
      // extract mouse click coordinates of the click from the event
      const { clientX, clientY } = e;
      console.log('showAnswerContextMenu', selectedPoint, clientX, clientY, e);
      // eslint-disable-next-line
      const veezooAnswerMessageDOM = $(e.target).parents('[data-interpretation-id]')[0];

      if (veezooAnswerMessageDOM && veezooAnswerMessageDOM.dataset) {
        const referenceTo = {
          interpretationId: veezooAnswerMessageDOM.dataset.interpretationId,
          partialAnswerId: veezooAnswerMessageDOM.dataset.partialAnswerId,
          answerId: veezooAnswerMessageDOM.dataset.answerId
        };
        // transform each x.value into a string (could be a string or a number), keep the rest of the object the same
        const referenceToPoint = selectedPoint.map(x => {
          // all strings in Highcharts plots are HTML-escaped (e.g. '&amp;'), which we undo here
          return { ...x, value: unescapeHtml(x.value.toString()) };
        });

        // dispatch a message containing the reference to the answer, the followUp snippets, and the mouse click coordinates
        this.props.dispatch(fetchAnswerContextMenu(referenceTo, referenceToPoint, { clientX, clientY }));
        trackEvent('Answer Context Menu Opened');
      }
    };
  }

  @boundMethod
  handleDrawerClose() {
    this.setState({ showVisualGraph: false });
    this.props.dispatch(setEntitiesSidebar({ isOpen: false }));
  }

  toggleMobileMenu = (isOpen = false) =>
    this.setState(prev => ({
      isMobileMenuOpen: isOpen || !prev.isMobileMenuOpen
    }));

  @boundMethod
  changeVisualModeTo(showVisualGraph) {
    this.setState({ showVisualGraph });
    changeUrlGivenCurrentQueryParams(this.props.history, showVisualGraph ? { visualMode: true } : { visualMode: null });
  }

  @boundMethod
  handleVisualGraphToggle() {
    this.changeVisualModeTo(!this.state.showVisualGraph);
    this.props.dispatch(setEntitiesSidebar({ isOpen: false }));
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.location.search !== prevProps.location.search) {
      this.toggleModals();
    }

    if (!prevState.showVisualGraph && this.state.showVisualGraph) {
      trackEvent('KG Visual Mode Opened', { knowledgeGraphId: this.props.meta.id });
      // remember that the KG component has been rendered
      if (!this.state.visualGraphHasRendered) {
        this.setState({ visualGraphHasRendered: true });
      }
    }

    if (prevState.showVisualGraph && !this.state.showVisualGraph) {
      trackEvent('KG Visual Mode Hidden', { knowledgeGraphId: this.props.meta.id });
    }

    // if the user has sent a message, hide visual mode (and remove showDialog param from url)
    if (prevProps.numberOfUserMessages !== this.props.numberOfUserMessages) {
      this.changeVisualModeTo(false);
      changeUrlGivenCurrentQueryParams(this.props.history, { showDialog: null });
    }
    // check if location has changed and if the sidebar should be shown
    if (
      this.props.location.pathname !== prevProps.location.pathname ||
      this.props.location.search !== prevProps.location.search
    ) {
      this.setState({
        showKgSidebar: shouldShowKgSidebar(this.props),
        showVisualGraph: shouldShowVisualGraph(this.props)
      });
    }
  }

  @boundMethod
  handleShowData(classUri) {
    this.props.dispatch(fetchDataForClass(classUri, this.props.t));
    this.props.dispatch(setEntitiesSidebar({ isOpen: false }));
  }

  @boundMethod
  handleSearch(e) {
    if (this.searchGraphTimeout) {
      clearTimeout(this.searchGraphTimeout);
    }
    // Delay search by 500ms
    this.searchGraphTimeout = setTimeout(
      (query, searchGraph, dispatch) => dispatch(searchGraph(query, 0)),
      500,
      e.target.value,
      searchGraph,
      this.props.dispatch
    );
  }

  closeEntitiesSidebar = () => this.props.dispatch(setEntitiesSidebar({ isOpen: false }));

  render() {
    const {
      showDiscovery,
      showChat,
      showTests,
      showSettings,
      selectedDiscoveryTopic,
      chatEntryPoint,
      isEmbedded,
      graphSearchResults,
      graph,
      meta,
      t,
      isOpenCustomerSelectionRoute,
      history
    } = this.props;

    const { showVisualGraph, visualGraphHasRendered, showKgSidebar } = this.state;

    const showChatComponent = showChat && !isOpenCustomerSelectionRoute;

    if (showKgSidebar && meta.isFetched && !meta.hasKnowledgeGraphSupport) {
      history.push('/chat');
    }

    const { sidebarNodeStructure, nodes, isLoadingGraph } = graph;

    return (
      <>
        <div className={styles.container} data-answer-tutorial="start">
          {!this.props.isMobile ? (
            <div className={styles.main}>
              <SplitSidebarView showKgSidebar={showKgSidebar} isEmbedded={isEmbedded}>
                <Sidebar
                  showChat={showChat}
                  showSettings={showSettings}
                  showGraph={showKgSidebar}
                  showDiscovery={showDiscovery}
                />
                <PageContent
                  showVisualGraph={showVisualGraph}
                  showKgSidebar={showKgSidebar}
                  showChat={showChat}
                  showChatComponent={showChatComponent}
                  showSettings={showSettings}
                  showTests={showTests}
                  showDiscovery={showDiscovery}
                  selectedDiscoveryTopic={selectedDiscoveryTopic}
                  chatEntryPoint={chatEntryPoint}
                  visualGraphHasRendered={visualGraphHasRendered}
                  displayModal={this.state.displayModal}
                />
              </SplitSidebarView>
              <KnowledgeGraphSidebar
                anchor="left"
                loading={isLoadingGraph}
                nodes={nodes}
                sidebarNodeStructure={sidebarNodeStructure}
                graphSearchResults={graphSearchResults}
                t={t}
                onClose={this.handleDrawerClose}
                onVisualGraphToggle={this.handleVisualGraphToggle}
                showVisualGraph={showVisualGraph}
                handleSearch={this.handleSearch}
                isEmbedded={isEmbedded}
                open={this.props.hasFetchedUser && showChatComponent && showKgSidebar}
                showData={this.handleShowData}
              />
            </div>
          ) : (
            <>
              <MobileHeader isMobileMenuOpen={this.state.isMobileMenuOpen} toggleMobileMenu={this.toggleMobileMenu} />
              <MobileDrawer open={this.state.isMobileMenuOpen} onClose={() => this.toggleMobileMenu(false)}>
                <Sidebar
                  showChat={showChat}
                  showSettings={showSettings}
                  showGraph={showKgSidebar}
                  showDiscovery={showDiscovery}
                />
              </MobileDrawer>
              <PageContent
                showVisualGraph={showVisualGraph}
                showKgSidebar={showKgSidebar}
                showChat={showChat}
                showChatComponent={showChatComponent}
                showSettings={showSettings}
                showTests={showTests}
                showDiscovery={showDiscovery}
                selectedDiscoveryTopic={selectedDiscoveryTopic}
                chatEntryPoint={chatEntryPoint}
                visualGraphHasRendered={visualGraphHasRendered}
                displayModal={this.state.displayModal}
              />
            </>
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps = state => ({
  username: state.user.username,
  language: state.user.language,
  numberOfUserMessages: state.chatMessages.filter(message => message.type === messageTypes.USER).length,
  recentlyAddedBoard: state.board.recentlyAddedBoard,
  graphSearchResults: state.graphSearchResults,
  meta: state.knowledgeGraphMeta.meta,
  graph: state.graph,
  isOpenCustomerSelectionRoute: state.customerSelections.isOpenCustomerSelectionRoute,
  hasUncompletedTutorial: state.tutorial.uncompletedTutorials.some(tut => tut.id === tutorialNames.OVERVIEW_TUTORIAL),
  hasFetchedUser: state.user.hasFetchedUser
});

export default withTranslation('veezoo')(connect(mapStateToProps)(withEmbedded(withRouter(Home))));
